/**
 * Wonders CCG online — main app component. Routes to match UI or redeem page
 * based on authentication status and beta access. M10 piece 1: invite gate.
 *
 * No styling yet — shadcn/ui + Tailwind 4 land at M7 (player UX). For
 * now this is wireframe-grade.
 */

import { DEMO_LANE_DECK } from '@wonders/games-demo-lane';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ApiKeysPage } from './admin/ApiKeysPage.js';
import { CardQualityPage } from './admin/CardQualityPage.js';
import { NeedsReviewPage } from './admin/NeedsReviewPage.js';
import { RulesAdminPage } from './admin/RulesAdminPage.js';
import { apiPath, createMatch, getMe, openMatchSocket, submitMove, type WSMessage } from './api.js';
import { FriendsPage } from './friends/FriendsPage.js';
import { Layout } from './Layout.js';
import { Lobby } from './Lobby.js';
import { ProfilePage } from './profile/ProfilePage.js';
import { Redeem } from './Redeem.js';
import { ReplayPlayer } from './replay/ReplayPlayer.js';
import { isViewerState, type PublicState } from './types.js';
import { DeckBrowser } from './wonders-table/components/DeckBrowser.js';
import { DeckBuilder } from './wonders-table/components/DeckBuilder.js';
import { WondersTable } from './wonders-table/index.js';

type Move = { kind: 'PlayUnit'; cardId: string; lane: 0 | 1 | 2 } | { kind: 'Pass' };

type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed';

const cardName = (cardId: string): string => {
  const card = DEMO_LANE_DECK.find((c) => c.id === cardId);
  return card ? `${card.name} (${card.cost}/${card.power})` : cardId;
};

/**
 * Render a lane's units as <li> elements, computing a stable per-unit
 * key from a running counter scoped to (laneId, owner, cardId). This
 * avoids array-index-as-key without changing the wire shape.
 */
const flattenLaneUnits = (
  lane: { units: Readonly<Record<string, readonly { cardId: string; power: number }[]>> },
  laneId: string,
  viewer: string | null,
): ReactElement[] => {
  const out: ReactElement[] = [];
  const counts = new Map<string, number>();
  for (const [owner, units] of Object.entries(lane.units)) {
    for (const u of units) {
      const slot = `${owner}-${u.cardId}`;
      const n = (counts.get(slot) ?? 0) + 1;
      counts.set(slot, n);
      out.push(
        <li key={`${laneId}-${slot}-${n}`}>
          {owner === viewer ? '🟢' : '🔴'} {cardName(u.cardId)} (P{u.power})
        </li>,
      );
    }
  }
  return out;
};

type NarrationState = {
  readonly active: boolean;
  readonly text: string;
};

/**
 * CloneDeckBuilder — small wrapper around DeckBuilder that fetches the
 * source deck on mount and feeds its cards + name (with " (copy)"
 * suffix) into the builder as a seed. The builder's Save then creates a
 * fresh private deck owned by the caller — the source is left alone.
 *
 * Doing the GET here (rather than inside DeckBuilder) keeps DeckBuilder
 * pure: it takes a seedDeck prop or doesn't, and never knows it's
 * cloning anything. Easier to test, easier to reuse for a future
 * "edit an in-progress draft" flow.
 */
function CloneDeckBuilder({
  sourceDeckId,
  onSaved,
  onCancel,
}: {
  sourceDeckId: string;
  onSaved: (deckId: string) => void;
  onCancel: () => void;
}): ReactElement {
  const [seed, setSeed] = useState<{
    name: string;
    cardIds: readonly string[];
    sourceName: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(apiPath(`/api/decks/${sourceDeckId}`), {
          credentials: 'include',
        });
        if (!r.ok) {
          setError(`Failed to load source deck (${r.status})`);
          return;
        }
        const j = (await r.json()) as {
          ok: boolean;
          deck?: { name: string; card_ids: readonly string[] };
        };
        if (cancelled || !j.ok || !j.deck) {
          if (!cancelled) setError('Source deck not found.');
          return;
        }
        const sourceName = j.deck.name;
        const suffix = ' (copy)';
        const trimmed =
          sourceName.length + suffix.length > 200
            ? sourceName.slice(0, 200 - suffix.length)
            : sourceName;
        setSeed({
          name: `${trimmed}${suffix}`,
          cardIds: j.deck.card_ids,
          sourceName,
        });
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceDeckId]);

  if (error) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <p className="text-sm text-rose-400">{error}</p>
        <button
          type="button"
          onClick={onCancel}
          className="mt-3 rounded bg-cosmos-700 px-3 py-1.5 text-sm hover:bg-cosmos-600"
        >
          Back
        </button>
      </div>
    );
  }
  if (!seed) {
    return <p className="p-8 text-center text-sm italic text-cosmos-500">Loading deck…</p>;
  }
  return <DeckBuilder seedDeck={seed} onSaved={onSaved} onCancel={onCancel} />;
}

export const App = (): ReactElement => {
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [, setBetaActive] = useState<boolean | null>(null);
  const [betaExpiresAt, setBetaExpiresAt] = useState<number | null>(null);
  const [needsRedeem, setNeedsRedeem] = useState<boolean>(false);
  const [matchUlid, setMatchUlid] = useState<string | null>(
    new URL(location.href).searchParams.get('match'),
  );
  // wonders-table is the Phase-1 HvH MVP and the default landing.
  // The legacy M4 demo-lane (Play-vs-Claude scaffold, pre-AvA) is still
  // reachable via ?mode=demo so we don't block any in-flight smoke tests
  // that assume the old shape; will be removed once the demo lane has
  // no callers.
  const tableMode = new URL(location.href).searchParams.get('mode') !== 'demo';
  const [opponentId, setOpponentId] = useState<string>('');
  const [state, setState] = useState<PublicState | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [log, setLog] = useState<string[]>([]);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [narration, setNarration] = useState<NarrationState>({ active: false, text: '' });

  // Get our own player id and beta status once.
  useEffect(() => {
    getMe()
      .then((m) => {
        setPlayerId(m.playerId);
        setBetaActive(m.betaActive ?? false);
        setBetaExpiresAt(m.betaExpiresAt ?? null);
        if (!m.betaActive) {
          setNeedsRedeem(true);
        }
      })
      .catch((e) => setLog((l) => [...l, `getMe error: ${String(e)}`]));
  }, []);

  // Connect WebSocket whenever matchUlid is set.
  useEffect(() => {
    if (!matchUlid) return;
    setStatus('connecting');
    const socket = openMatchSocket(
      matchUlid,
      (msg: WSMessage) => {
        if (msg.kind === 'welcome' || msg.kind === 'event_delta') {
          setState(msg.publicState as PublicState);
          if (msg.kind === 'event_delta') {
            setLog((l) => [...l.slice(-19), `seq=${msg.seq}: ${msg.events.length} event(s)`]);
          } else {
            setLog((l) => [...l.slice(-19), `welcome — viewer ${msg.viewer}`]);
          }
        } else if (msg.kind === 'move_rejected') {
          setLog((l) => [...l.slice(-19), `move rejected: ${msg.reason}`]);
        } else if (msg.kind === 'error') {
          setLog((l) => [...l.slice(-19), `error: ${msg.message}`]);
        } else if (msg.kind === 'narration_delta') {
          setNarration((prev) => ({
            active: true,
            text: prev.text + msg.text,
          }));
        } else if (msg.kind === 'narration_complete') {
          setNarration((prev) => ({
            active: false,
            text: prev.text,
          }));
        }
      },
      () => setStatus('closed'),
    );
    socket.addEventListener('open', () => setStatus('open'));
    setWs(socket);
    return () => {
      socket.close();
    };
  }, [matchUlid]);

  const startMatch = useCallback(async (opponent: string | undefined) => {
    try {
      const { matchUlid: ulid } = await createMatch(opponent);
      const url = new URL(location.href);
      url.searchParams.set('match', ulid);
      history.pushState({}, '', url.toString());
      setMatchUlid(ulid);
    } catch (e) {
      setLog((l) => [...l, `createMatch error: ${String(e)}`]);
    }
  }, []);

  const onCreateMatch = useCallback(
    () => startMatch(opponentId || undefined),
    [startMatch, opponentId],
  );

  const onPlayVsAi = useCallback(() => startMatch('@ai-claude'), [startMatch]);

  const send = useCallback(
    (move: Move) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ kind: 'submit_move', move }));
      } else {
        // Fallback to REST if WS isn't ready.
        if (matchUlid) void submitMove(matchUlid, move);
      }
    },
    [ws, matchUlid],
  );

  // If beta access is needed, show the redeem page instead
  if (needsRedeem) {
    return (
      <Layout>
        <Redeem
          onSuccess={() => {
            setNeedsRedeem(false);
            setBetaActive(true);
          }}
        />
      </Layout>
    );
  }

  // Path-based dispatch — keeps us off a router lib until the surface
  // area justifies one. Order matters: more-specific patterns first.
  const replayMatch = location.pathname.match(/^\/replay\/([A-Z0-9_-]+)\/?$/i);
  if (replayMatch && playerId) {
    const ulid = replayMatch[1];
    if (ulid)
      return (
        <Layout>
          <ReplayPlayer matchUlid={ulid} />
        </Layout>
      );
  }
  // Spectator UI is wired backend-side (GET /api/match/:ulid/spectate)
  // but the Table renderer doesn't yet have a redacted-state mode. The
  // route is reserved here for the next Phase 2e generation.
  const profileMatch = location.pathname.match(/^\/u\/([^/]+)\/?$/);
  if (profileMatch) {
    const handle = decodeURIComponent(profileMatch[1] ?? '');
    if (handle)
      return (
        <Layout>
          <ProfilePage handle={handle} />
        </Layout>
      );
  }
  if (location.pathname === '/friends') {
    return (
      <Layout>
        <FriendsPage />
      </Layout>
    );
  }
  if (location.pathname === '/admin/needs-review') {
    return (
      <Layout>
        <NeedsReviewPage />
      </Layout>
    );
  }
  if (location.pathname === '/admin/card-quality') {
    return (
      <Layout>
        <CardQualityPage />
      </Layout>
    );
  }
  if (location.pathname === '/admin/rules') {
    return (
      <Layout>
        <RulesAdminPage />
      </Layout>
    );
  }
  if (location.pathname === '/admin/api-keys') {
    return (
      <Layout>
        <ApiKeysPage />
      </Layout>
    );
  }

  // Match-creation helper used by both the Practice-vs-AI and Play-Online
  // lobby buttons. The variant + opponent shape diverge but the POST is
  // shared. For vs-AI matches we also pick a deck for the AI seat —
  // without it the AI starts with empty zones and has nothing to play.
  // Picks a tournament deck at random (excluding the player's pick to
  // avoid a mirror match).
  const createTableMatch = async (deckId: string, opts: { vsAi?: boolean }) => {
    let opponentDeckId: string | undefined;
    if (opts.vsAi) {
      try {
        const dRes = await fetch(apiPath('/api/decks?game_id=wonders'), {
          credentials: 'include',
        });
        const dJson = (await dRes.json()) as {
          ok: boolean;
          decks: { deck_id: string; is_tournament: boolean }[];
        };
        const tournaments = (dJson.decks ?? []).filter(
          (d) => d.is_tournament && d.deck_id !== deckId,
        );
        const pick = tournaments[Math.floor(Math.random() * tournaments.length)];
        opponentDeckId = pick?.deck_id;
      } catch {
        // Fall through with undefined — server will hand the AI an
        // empty deck if the lookup fails. Surfaced as an empty hand.
      }
    }
    const res = await fetch(apiPath('/api/match'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        gameId: 'wonders-table',
        deckId,
        ...(opts.vsAi ? { opponent: '@ai-claude' } : {}),
        ...(opponentDeckId ? { opponentDeckId } : {}),
        variant: 'casual',
      }),
    });
    const data = (await res.json()) as { matchUlid?: string };
    if (!data.matchUlid) return;
    const url = new URL(location.href);
    url.searchParams.set('match', data.matchUlid);
    url.searchParams.set('mode', 'table');
    history.pushState({}, '', url.toString());
    setMatchUlid(data.matchUlid);
  };

  // wonders-table mode is the default. The in-match Table goes full-bleed
  // (no Layout chrome); the lobby wraps in Layout for banner + footer.
  if (tableMode && playerId) {
    if (matchUlid) {
      return <WondersTable matchUlid={matchUlid} viewer={playerId} />;
    }
    // Deck routes are signalled via `?deck=...` rather than pathname
    // segments so we don't have to wire a router yet. A small
    // force-render bump (setOpponentId) makes App re-evaluate this branch
    // after history.pushState.
    const url0 = new URL(location.href);
    const deckMode = url0.searchParams.get('deck');
    const cloneSource = url0.searchParams.get('source');
    const closeDeckScreen = (): void => {
      const url = new URL(location.href);
      url.searchParams.delete('deck');
      url.searchParams.delete('source');
      history.pushState({}, '', url.toString());
      setOpponentId((s) => (s === '' ? ' ' : ''));
    };
    const navigateToDeckScreen = (params: Record<string, string>): void => {
      const url = new URL(location.href);
      // Reset any stale deck params before applying the new ones.
      url.searchParams.delete('deck');
      url.searchParams.delete('source');
      for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
      history.pushState({}, '', url.toString());
      setOpponentId((s) => (s === '' ? ' ' : ''));
    };
    if (deckMode === 'new') {
      return (
        <Layout>
          <DeckBuilder onSaved={() => closeDeckScreen()} onCancel={() => closeDeckScreen()} />
        </Layout>
      );
    }
    if (deckMode === 'browse') {
      return (
        <Layout>
          <DeckBrowser
            onCreateNew={() => navigateToDeckScreen({ deck: 'new' })}
            onCloneDeck={(sourceId) => navigateToDeckScreen({ deck: 'clone', source: sourceId })}
            onBack={closeDeckScreen}
          />
        </Layout>
      );
    }
    if (deckMode === 'clone' && cloneSource) {
      return (
        <Layout>
          <CloneDeckBuilder
            sourceDeckId={cloneSource}
            onSaved={() => closeDeckScreen()}
            onCancel={() => closeDeckScreen()}
          />
        </Layout>
      );
    }
    return (
      <Layout>
        <Lobby
          onPlayOnline={(deckId) => void createTableMatch(deckId, {})}
          onPracticeAi={(deckId) => void createTableMatch(deckId, { vsAi: true })}
          onCreateDeck={() => navigateToDeckScreen({ deck: 'new' })}
          onBrowseDecks={() => navigateToDeckScreen({ deck: 'browse' })}
          onCloneDeck={(sourceId) => navigateToDeckScreen({ deck: 'clone', source: sourceId })}
          onCardBrowser={() => {
            const url = new URL(location.href);
            url.pathname = '/cards';
            history.pushState({}, '', url.toString());
            window.location.reload();
          }}
        />
      </Layout>
    );
  }

  const meState = playerId && state ? state.players[playerId] : null;
  const isMyTurn = state?.activePlayer === playerId;

  // Calculate days until beta expires
  const daysUntilExpiry =
    betaExpiresAt && !needsRedeem
      ? Math.ceil((betaExpiresAt - Math.floor(Date.now() / 1000)) / 86400)
      : null;

  return (
    <main style={{ fontFamily: 'system-ui, sans-serif', padding: 16, maxWidth: 800 }}>
      <h1>Wonders — Demo Lane</h1>

      {daysUntilExpiry && daysUntilExpiry <= 7 && daysUntilExpiry > 0 ? (
        <div
          style={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            padding: '12px 16px',
            borderRadius: 4,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          Beta access expires in {daysUntilExpiry} day{daysUntilExpiry === 1 ? '' : 's'}.
        </div>
      ) : null}

      <p style={{ fontSize: 13, color: '#666' }}>
        Player: <code>{playerId ?? '…'}</code>
        {matchUlid ? (
          <>
            {' '}
            · Match: <code>{matchUlid}</code> · WS: <code>{status}</code>
          </>
        ) : null}
      </p>

      {!matchUlid ? (
        <section>
          <h2>Start a match</h2>
          <button
            type="button"
            onClick={onPlayVsAi}
            style={{
              padding: '8px 16px',
              fontSize: 14,
              background: '#a36a30',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Play vs Claude
          </button>
          <p style={{ marginTop: 16 }}>
            Or play against another human (paste their player id, or leave empty to create a
            1-seat-open match you can share via URL):
          </p>
          <input
            type="text"
            value={opponentId}
            onChange={(e) => setOpponentId(e.target.value)}
            placeholder="dev-XXXXXXXX"
            style={{ width: 240, padding: 4 }}
          />
          <button type="button" onClick={onCreateMatch} style={{ marginLeft: 8 }}>
            Create match
          </button>
        </section>
      ) : null}

      {state ? (
        <section>
          <h2>
            Round {state.round} {isMyTurn ? '— your turn' : `— ${state.activePlayer}'s turn`}
          </h2>

          {narration.text ? (
            <div
              style={{
                background: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: 4,
                padding: 8,
                marginBottom: 12,
                fontSize: 13,
                fontFamily: 'system-ui, sans-serif',
              }}
            >
              <div style={{ fontWeight: 500, marginBottom: 4 }}>Claude's reasoning:</div>
              <div>{narration.text}</div>
              {narration.active ? (
                <div style={{ display: 'inline-block', marginLeft: 4 }}>
                  <span style={{ animation: 'blink 1s infinite', display: 'inline-block' }}>▌</span>
                </div>
              ) : null}
            </div>
          ) : null}

          <h3>Lanes</h3>
          <div style={{ display: 'flex', gap: 12 }}>
            {(['L0', 'L1', 'L2'] as const).map((laneId, idx) => {
              const lane = state.lanes[idx];
              if (!lane) return null;
              return (
                <div
                  key={laneId}
                  style={{
                    border: '1px solid #aaa',
                    padding: 8,
                    flex: 1,
                    minHeight: 80,
                  }}
                >
                  <strong>Lane {idx}</strong>
                  {lane.controller ? (
                    <div style={{ fontSize: 12 }}>controlled by {lane.controller}</div>
                  ) : null}
                  <ul style={{ paddingLeft: 16, fontSize: 13 }}>
                    {flattenLaneUnits(lane, laneId, playerId)}
                  </ul>
                </div>
              );
            })}
          </div>

          {meState && isViewerState(meState) ? (
            <>
              <h3>
                Your hand — mana {meState.mana} / deck {meState.deck.length}
              </h3>
              <ul>
                {meState.hand.map((cardId) => (
                  <li key={cardId}>
                    {cardName(cardId)}
                    {[0, 1, 2].map((laneIdx) => (
                      <button
                        type="button"
                        key={laneIdx}
                        disabled={!isMyTurn}
                        onClick={() =>
                          send({ kind: 'PlayUnit', cardId, lane: laneIdx as 0 | 1 | 2 })
                        }
                        style={{ marginLeft: 6 }}
                      >
                        → L{laneIdx}
                      </button>
                    ))}
                  </li>
                ))}
              </ul>
              <button type="button" disabled={!isMyTurn} onClick={() => send({ kind: 'Pass' })}>
                Pass
              </button>
            </>
          ) : (
            <p>You're spectating or viewing as the opponent.</p>
          )}
        </section>
      ) : null}

      <section>
        <h3>Log</h3>
        <pre style={{ fontSize: 11, background: '#f4f4f4', padding: 8, maxHeight: 200 }}>
          {log.join('\n')}
        </pre>
      </section>
    </main>
  );
};
